import type { ReactNode } from 'react';
import type { Root, RootOptions } from 'react-dom/client';
import { createRoot, hydrateRoot } from 'react-dom/client';

import AuthoringHelper from '@/utils/authoring';
import type { BootstrapOptions } from '@/utils/bootstrap/typings';
import Entry from '@/utils/entry';
import EntryPointsHelper from '@/utils/entry-points-helper';
import type { PageModel } from '@adobe/aem-react-editable-components';
import { ModelClient, ModelManager } from '@adobe/aem-spa-page-model-manager';
import { ModelManagerConfiguration } from '@adobe/aem-spa-page-model-manager/dist/ModelManager';
import { Theme } from '@emotion/react';
import { loadableReady } from '@loadable/component';
import { ComponentsManager } from '@multitenancy-spa/components-manager/esm/services/components-manager';
import { EnvVarsService } from '@multitenancy-spa/env-vars-service';

class Bootstrap {
	private static instance: Bootstrap;
	private rootOptions?: RootOptions;
	private root: Root | undefined;
	private _componentsManager?: ComponentsManager;
	private theme: Theme;
	private BeforeEntryComponent?: ReactNode | ReactNode[];
	private AfterEntryComponent?: ReactNode | ReactNode[];
	public isInitialized = false;

	constructor({
		rootOptions,
		componentsManager,
		theme,
		BeforeEntryComponent,
		AfterEntryComponent,
	}: BootstrapOptions) {
		this.rootOptions = rootOptions;
		this._componentsManager = componentsManager;
		this.theme = this.setupTheme(theme);
		this.BeforeEntryComponent = BeforeEntryComponent;
		this.AfterEntryComponent = AfterEntryComponent;

		Bootstrap.instance = this;
	}

	public static getInstance() {
		return this.instance;
	}

	public get componentsManager(): ComponentsManager | undefined {
		return this._componentsManager;
	}

	private setupTheme(theme: Theme) {
		let remoteUrl = EntryPointsHelper.entryPoint?.dataset.remoteUrl;

		if (!remoteUrl) {
			const previousElement = EntryPointsHelper.entryPoint
				?.previousElementSibling as HTMLElement;
			remoteUrl = previousElement?.dataset.remoteUrl;
		}

		if (remoteUrl) {
			return {
				...theme,
				remoteUrl,
			};
		}

		return theme;
	}

	static generateModelPath(
		fragmentVariationPath?: string,
	): string | undefined {
		if (!fragmentVariationPath) return undefined;
		const isAbsolutePath = fragmentVariationPath.startsWith('http');
		let origin = globalThis.location.origin;

		if (
			!isAbsolutePath &&
			EntryPointsHelper.entryPoint?.dataset.modelPath &&
			EntryPointsHelper.entryPoint?.dataset.modelPath.startsWith('http')
		) {
			origin = new URL(EntryPointsHelper.entryPoint?.dataset.modelPath)
				.origin;
		}

		const generateModelPath = isAbsolutePath
			? fragmentVariationPath
			: `${origin}${fragmentVariationPath}`;
		return generateModelPath;
	}

	private render(): boolean {
		if (
			document.readyState === 'complete' &&
			EntryPointsHelper.entryPoint
		) {
			const needsHudration = EntryPointsHelper.entryPoint.hasChildNodes();
			const hydrationEnabled = EnvVarsService.getVar(
				'VITE_ENABLE_HYDRATION',
				true,
			);

			const { BeforeEntryComponent, AfterEntryComponent } = this;

			if (needsHudration && hydrationEnabled) {
				loadableReady(() => {
					if (EntryPointsHelper.entryPoint) {
						this.root = hydrateRoot(
							EntryPointsHelper.entryPoint,
							<Entry
								fragmentVariationPath={
									EntryPointsHelper.entryPoint?.dataset
										.modelPath
								}
								theme={this.theme}
								BeforeEntryComponent={BeforeEntryComponent}
								AfterEntryComponent={AfterEntryComponent}
							/>,
						);
					}
				});
			} else {
				this.root = createRoot(
					EntryPointsHelper.entryPoint,
					this.rootOptions,
				);

				this.root.render(
					<Entry
						fragmentVariationPath={
							EntryPointsHelper.entryPoint?.dataset.modelPath
						}
						theme={this.theme}
						BeforeEntryComponent={BeforeEntryComponent}
						AfterEntryComponent={AfterEntryComponent}
					/>,
				);
			}

			return true;
		}
		return false;
	}

	static validateModelPath(injectedModelPath?: string): URL | false {
		let fragmentVariationPath =
			injectedModelPath ??
			EntryPointsHelper.entryPoint?.dataset.modelPath;

		if (fragmentVariationPath)
			fragmentVariationPath = Bootstrap.generateModelPath(
				fragmentVariationPath,
			);

		if (!fragmentVariationPath) return false;

		try {
			return new URL(fragmentVariationPath);
		} catch {
			return false;
		}
	}

	public setupModelPath(
		fragmentVariationPath?: string,
		mockedModel?: PageModel,
		callback?: (model: PageModel) => void,
	): Promise<PageModel> {
		return new Promise(async (resolve, reject) => {
			let model = mockedModel;

			try {
				const validModelPath =
					Bootstrap.validateModelPath(fragmentVariationPath) ||
					new URL(globalThis.location.href);

				const modelClient = new ModelClient(validModelPath.origin);

				let modelManagerConfig: ModelManagerConfiguration = {
					path: validModelPath.pathname,
					modelClient,
				};

				if (model) {
					modelManagerConfig = {
						...modelManagerConfig,
						model,
					};
				}

				if (!model) {
					if (!AuthoringHelper.isInEditor && this.isInitialized) {
						model =
							await ModelManager.getData<PageModel>(
								modelManagerConfig,
							);
					} else {
						model =
							await ModelManager.initialize<PageModel>(
								modelManagerConfig,
							);
						this.componentsManager?.map();
						this.isInitialized = true;

						if (AuthoringHelper.isInEditor) {
							const { default: AuthoringActions } = await import(
								'@/utils/authoring/actions'
							);

							AuthoringActions.handleUiNormalisation();
						}
					}
				}

				if (model && callback) {
					callback(model);
				}

				resolve(model);
			} catch (error) {
				reject(error);
			}
		});
	}

	public init(): Promise<'sync' | 'onReady'> {
		return new Promise(async (resolve, reject) => {
			try {
				if (globalThis.started) reject('Already started');

				globalThis.started = true;

				const isLocal = EnvVarsService.getVar('VITE_IS_LOCAL', true);
				const isLocallyOverriding = EnvVarsService.getVar(
					'VITE_IS_LOCALLY_OVERRIDING',
					true,
				);
				const usingMocks = EnvVarsService.getVar(
					'VITE_USE_MOCKS',
					true,
				);

				if (isLocal && usingMocks && !isLocallyOverriding) {
					const { default: mockedModel } = await import(
						'./../../../vite-mocked-endpoints/top-level-navigation.model.json'
					);

					await ModelManager.initialize<PageModel>({
						model: mockedModel,
					});
					this.componentsManager?.map();
					this.isInitialized = true;
				}

				const rendered = this.render();

				if (rendered) {
					resolve('sync');
				} else {
					document.onreadystatechange = () => {
						this.render();
						resolve('onReady');
					};
				}
			} catch (error) {
				console.log(error);
				reject(error);
			}
		});
	}
}

export default Bootstrap;
